import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { myNav } from '@/components/px/nav'
import { selectActiveOrgId, selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { fetchProjectHours, hoursErrorDismissed, updateHours } from '@/features/hours/slices/hoursSlice'
import {
  selectHoursError,
  selectHoursMutatingIds,
  selectProjectHoursLoading,
  selectSubmittedByProject,
} from '@/features/hours/selectors'
import { recordDecisionNotification } from '@/features/hours/services/decisionNotification'
import type { HoursEntry } from '@/features/hours/types'
import { formatDay, formatMinutes } from '@/lib/money'
import { fetchProjects, selectProjectsItems } from '@/features/projects/slices/projectsSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * APPROVALS (`/my/approvals`, PX-W6.4 AC-2): a project's submitted entries
 * and the two decisions a decider records — Confirm and Send back.
 *
 * Decision D-2 (the API has no `approved` state): Confirm = `PUT
 * status:"submitted"` (an idempotent round-trip whose returned row evidences
 * the decision and stays in the counted state); Send back = `PUT
 * status:"draft"`. The PUT is a FULL REPLACE, so it echoes the row's own
 * mutable fields and changes only `status`. The durable decision record is
 * the notification: exactly ONE `POST /notifications` addressed to the
 * entry's author, fired only after the PUT fulfilled, riding the existing
 * bus (`notification.created`) into W6.2's centre. A failed notification POST
 * surfaces as an error — never silent — while the state change stands (the
 * server truth is already in). No "approved" word exists on this surface.
 */

/** The two decisions — named by what the SUBMITTER needs to know. */
type Decision = 'confirmed' | 'sent-back'

/** Stable empty list — never allocate per selector call (see hours/selectors). */
const NO_ENTRIES: HoursEntry[] = []

export default function ApprovalsPage() {
  const { t } = useTranslation('commercial')
  const dispatch = useAppDispatch()
  const role = useAppSelector(selectActiveRoleKey)
  const orgId = useAppSelector(selectActiveOrgId)
  const projects = useAppSelector(selectProjectsItems)
  const loading = useAppSelector(selectProjectHoursLoading)
  const error = useAppSelector(selectHoursError)
  const mutatingIds = useAppSelector(selectHoursMutatingIds)

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [notifyError, setNotifyError] = useState<string | null>(null)

  useEffect(() => {
    if (orgId === null) return
    void dispatch(fetchProjects(orgId))
  }, [dispatch, orgId])

  // The default selection is a pure projection of the loaded list — the
  // first readable project until the user picks one explicitly. Computed
  // during render instead of an Effect: no extra render round-trip, and no
  // cascading setState-in-effect.
  const projectId = selectedProjectId ?? (projects.length > 0 ? projects[0]!.id : null)

  useEffect(() => {
    if (projectId === null) return
    void dispatch(fetchProjectHours(projectId))
  }, [dispatch, projectId])

  const submitted = useAppSelector((s) =>
    projectId === null ? NO_ENTRIES : selectSubmittedByProject(s, projectId)
  )

  const onRetryClicked = (): void => {
    // Dismiss FIRST — it re-arms the cache (the fetchCache Retry contract);
    // see the timesheet's twin for why the order matters.
    dispatch(hoursErrorDismissed())
    if (projectId !== null) void dispatch(fetchProjectHours(projectId))
  }

  const onDecisionClicked = async (entry: HoursEntry, decision: Decision): Promise<void> => {
    if (projectId === null) return
    const result = await dispatch(
      updateHours({
        projectId,
        entryId: entry.id,
        input: {
          // Full replace: every mutable field echoed verbatim; only the
          // status moves (decision D-2).
          versionId: entry.versionId,
          workPackageRef: entry.workPackageRef,
          day: entry.day,
          minutes: entry.minutes,
          billable: entry.billable,
          note: entry.note,
          status: decision === 'confirmed' ? 'submitted' : 'draft'
        }
      })
    )
    if (!updateHours.fulfilled.match(result)) return
    // Exactly one notification per decision, only after the state change
    // landed (I-3). A failure here is surfaced below, never swallowed.
    try {
      await recordDecisionNotification(entry, {
        title: t('commercial:approvals.notification.title', { day: entry.day }),
        description:
          decision === 'confirmed'
            ? t('commercial:approvals.notification.confirmed')
            : t('commercial:approvals.notification.sentBack')
      })
      setNotifyError(null)
    } catch {
      setNotifyError(t('commercial:approvals.notification.failed'))
    }
  }

  const inputClass =
    'rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

  return (
    <AppShell
      moduleLabel={t('commercial:chrome.module')}
      breadcrumb={<span>{t('commercial:approvals.title')}</span>}
      contextPill={t('commercial:chrome.myWork').toUpperCase()}
      nav={myNav(role)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('commercial:approvals.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('commercial:approvals.lede')}</p>
      </header>

      {notifyError !== null && (
        <p role="alert" className="mt-4 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          {notifyError}
        </p>
      )}

      {projects.length === 0 ? (
        <div className="mt-5">
          <EmptyPanel
            title={t('commercial:approvals.noProjects.title')}
            body={t('commercial:approvals.noProjects.body')}
          />
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <label htmlFor="approvals-project" className="text-sm font-medium text-foreground">
              {t('commercial:approvals.project')}
            </label>
            <select
              id="approvals-project"
              value={projectId ?? ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className={inputClass}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <section className="mt-4" aria-label={t('commercial:approvals.queue.title')}>
            <SectionLabel>{t('commercial:approvals.queue.title')}</SectionLabel>
            <div className="mt-2">
              {loading ? (
                <LoadingPanel />
              ) : error !== null && error.status === 403 ? (
                // AC-6.2: the caller may not decide this project's hours.
                <ForbiddenPanel requested="/{orgId}/projects/:projectId/hours" />
              ) : error !== null ? (
                <ErrorPanel error={asApiError(error)} onRetryClicked={onRetryClicked} />
              ) : submitted.length === 0 ? (
                <EmptyPanel
                  title={t('commercial:approvals.empty.title')}
                  body={t('commercial:approvals.empty.body')}
                />
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border bg-card">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="v-row border-b border-border">
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:approvals.table.day')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:approvals.table.minutes')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:approvals.table.billable')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:approvals.table.note')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:approvals.table.decisions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {submitted.map((entry) => {
                        const mutating = mutatingIds.includes(entry.id)
                        return (
                          <tr key={entry.id} className="v-row border-b border-border last:border-b-0">
                            <td className="px-3 py-2.5 text-foreground">{formatDay(entry.day)}</td>
                            <td className="v-figure px-3 py-2.5 text-right tabular-nums text-foreground">
                              {formatMinutes(entry.minutes)}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground">
                              {entry.billable ? t('commercial:common.yes') : t('commercial:common.no')}
                            </td>
                            <td className="max-w-[24rem] px-3 py-2.5 text-muted-foreground">
                              {entry.note ?? '—'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-right">
                              <span className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => void onDecisionClicked(entry, 'sent-back')}
                                  disabled={mutating}
                                  className="rounded-md border border-input px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
                                >
                                  {t('commercial:approvals.sendBack')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void onDecisionClicked(entry, 'confirmed')}
                                  disabled={mutating}
                                  className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                >
                                  {t('commercial:approvals.confirm')}
                                </button>
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </AppShell>
  )
}
